<script lang="ts" setup>
import { computed } from 'vue';
import { $fetch } from '@/home/plugins/fetch';
import { useAppStore } from '@/home/stores/app';
import { useAsyncData } from '@/home/hooks/async-data';
import { statsPv } from '@/home/utils';

const props = defineProps({
  navigator: {
    type: String,
    default: '',
  },
});

const tabs = [
  {
    name: 'regulation',
    text: '管理制度与证照',
    type: 1,
  },
  {
    name: 'work',
    text: '工作报告',
    type: 2,
  },
  {
    name: 'audit',
    text: '审计报告',
    type: 3,
  },
  {
    name: 'bidding',
    text: '招投标信息公示',
    type: 4,
  },
  {
    name: 'insight',
    text: '开源态势洞察',
  },
];

const appStore = useAppStore();

const pathPrefix = '/information/';
const defaultTitle = '信息公开';

const getCurrentTab = () => tabs.find((item) => item.name === props.navigator) ?? tabs[0];
const getTitle = () => `${getCurrentTab().text} - ${defaultTitle}`;

const getInfoType = () => {
  if (props.navigator === 'insight') {
    return tabs[0].type;
  }

  return getCurrentTab().type;
}

const { data: listing } = useAsyncData(
  'information',
  () => props.navigator === 'insight' ?
    $fetch('/insight/v1/list', { data: { limit: -1 } })
    : $fetch('/public/information/v1/list', { data: { infoType: getInfoType() } }),
  {
    cache: false,
    watch: [() => props.navigator],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (!handleSuccess) {
        return;
      }

      if (serverContext) {
        serverContext.title = getTitle();
      }

      if (!import.meta.env.SSR) {
        appStore.setTitle(getTitle());

        // 统计埋点
        statsPv();
      }
    },
  },
);

const selectedIndex = computed(() => {
  const index = tabs.findIndex((item) => item.name === props.navigator);

  return index < 0 ? 0 : index;
});
</script>

<template>
  <main class="page-information min-h-[1554px] bg-white pb-[160px]">
    <section class="page-information-banner h-[400px] w-full">
      <div class="mx-auto w-[1360px] pt-[143px] text-[#2d2d2d] <2xl:w-1200px">
        <h1 class="mb-[18px] mt-0 text-[50px] font-medium">信息公开</h1>
        <span class="block max-w-800px text-[24px] font-normal leading-30px <2xl:max-w-600px">包括基金会的信息透明公开，保证基金会的公益属性和慈善属性，还包含开源行业知识的开源共享</span>
      </div>
    </section>
    <section class="mx-auto w-1360px <2xl:w-1200px">
      <div class="relative mt-[48px] h-[54px] w-full flex justify-between">
        <RouterLink
          v-for="(item, index) in tabs"
          :key="item.name"
          :to="`${pathPrefix}${item.name}`"
          class="block h-[36px] w-[calc(100%/5)] text-center text-[24px] leading-[36px] no-underline focus-visible:outline-unset"
          :class="[selectedIndex === index ? 'text-[#1180FF]' : 'text-[#2D2D2D]']"
        >
          {{ item.text }}
        </RouterLink>

        <div
          class="absolute bottom-0 left-0 h-1px w-[calc(100%/5)] bg-[#1180ff] transition-transform duration-300"
          :style="{ transform: `translateX(${selectedIndex * 100}%)` }"
        >
        </div>
      </div>

      <div class="pt-[48px] text-[30px] text-white font-medium">
        <div class="flex flex-wrap items-start">
          <template v-if="props.navigator === 'insight'">
            <div
              v-for="(piece, inx) in listing"
              :key="`${tabs[selectedIndex]}-${piece.id}`"
              class="open-card mb-[32px] h-420px w-420px <2xl:h-400px <2xl:w-400px"
              :class="[(inx + 1) % 3 ? 'mr-[48px] <2xl:mr-0' : '']"
            >
              <a :href="piece.fileUrl" target="_blank" class="absolute z-1 bg-transparent no-underline">阅读完整报告</a>
              <div class="ml-[92px] mt-[90px] h-[24px] text-[14px] leading-[24px]">{{ piece.title }}</div>
              <div class="ml-[92px] h-[36px] text-[24px] leading-[36px] tracking-[-2.4px]">开源态势洞察</div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="(piece, inx) in listing"
              :key="`${tabs[selectedIndex]}-${piece.id}`"
              class="rule-card mb-[32px] h-[420px] w-[420px] <2xl:h-400px <2xl:w-400px"
              :class="[(inx as number + 1) % 3 ? 'mr-[48px] <2xl:mr-0' : '']"
            >
              <a :href="piece.url" target="_blank" class="absolute z-1 bg-transparent no-underline">阅读完整报告</a>
              <div class="ml-[96px] mt-[200px] w-[216px] text-center <2xl:ml-86px">
                {{ piece.title }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="scss" scoped>
.page-information {
  .page-information-banner {
    background: url(@/home/assets/information/banner-bg.jpg) no-repeat;
    background-position: center;
    background-size: cover;
  }

  .rule-card,
  .open-card {
    position: relative;
    background-repeat: no-repeat;
    background-position: left top;
    background-size: cover;

    &::after {
      position: absolute;
      top: 30px;
      content: "";

      @apply left-76px w-252px h-364px <2xl:left-72px <2xl:w-240px <2xl:h-346px;
    }

    a {
      top: 187px;
      display: none;
      padding: 10px 28px;
      color: #fff;
      font-size: 20px;
      border: 1px solid #fff;
      border-radius: 10000px;

      @apply left-114px <2xl:left-108px;
    }

    &:hover {
      &::after {
        background-color: rgba(45, 45, 45, 0.6);
      }

      a {
        display: block;
      }
    }
  }

  .rule-card {
    background-image: url(@/home/assets/information/book-bg.png);
  }

  .open-card {
    background-image: url(@/home/assets/information/open-book.png);
  }
}
</style>
